export const SCANNER_SUCCESS_ANIMATION_SRC = '/assets/rive/success-animation.riv';

const WARMUP_CANVAS_SIZE = 144;
const WARMUP_TIMEOUT_MS = 8000;

let riveRuntimePromise = null;
let riveBufferPromise = null;
let riveWarmupPromise = null;

export function loadScannerSuccessRiveRuntime() {
  if (!riveRuntimePromise) {
    riveRuntimePromise = import('@rive-app/canvas').catch(error => {
      riveRuntimePromise = null;
      throw error;
    });
  }

  return riveRuntimePromise;
}

export function loadScannerSuccessRiveBuffer() {
  if (typeof fetch !== 'function') return Promise.resolve(null);

  if (!riveBufferPromise) {
    riveBufferPromise = fetch(SCANNER_SUCCESS_ANIMATION_SRC)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to preload scanner success animation: ${response.status}`);
        }

        return response.arrayBuffer();
      })
      .catch(error => {
        riveBufferPromise = null;
        throw error;
      });
  }

  return riveBufferPromise;
}

export async function getScannerSuccessRiveSource() {
  const riveBuffer = await loadScannerSuccessRiveBuffer().catch(() => null);
  return riveBuffer ? { buffer: riveBuffer.slice(0) } : { src: SCANNER_SUCCESS_ANIMATION_SRC };
}

export function preloadScannerSuccessAnimation() {
  if (!riveWarmupPromise) {
    riveWarmupPromise = warmScannerSuccessAnimation().catch(() => {
      riveWarmupPromise = null;
      return null;
    });
  }

  return riveWarmupPromise;
}

async function warmScannerSuccessAnimation() {
  if (typeof document === 'undefined') {
    return Promise.all([loadScannerSuccessRiveRuntime(), loadScannerSuccessRiveBuffer()]).catch(
      () => null,
    );
  }

  const [riveRuntime, riveSource, body] = await Promise.all([
    loadScannerSuccessRiveRuntime(),
    getScannerSuccessRiveSource(),
    getDocumentBody(),
  ]);

  if (!body) return null;

  const { Alignment, Fit, Layout, Rive } = riveRuntime;
  const canvas = document.createElement('canvas');
  canvas.width = WARMUP_CANVAS_SIZE;
  canvas.height = WARMUP_CANVAS_SIZE;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  body.appendChild(canvas);

  return new Promise(resolve => {
    let riveAnimation = null;
    let complete = false;

    const finish = () => {
      if (complete) return;
      complete = true;

      if (riveAnimation) {
        riveAnimation.cleanup();
        riveAnimation = null;
      }

      canvas.remove();
      resolve(null);
    };

    const finishAfterPaint = () => {
      const scheduleFrame =
        typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame
          : callback => setTimeout(callback, 0);
      scheduleFrame(() => scheduleFrame(finish));
    };

    const timeout = setTimeout(finish, WARMUP_TIMEOUT_MS);

    try {
      riveAnimation = new Rive({
        ...riveSource,
        canvas,
        autoplay: true,
        layout: new Layout({
          fit: Fit.Contain,
          alignment: Alignment.Center,
        }),
        onLoad: () => {
          clearTimeout(timeout);
          finishAfterPaint();
        },
        onLoadError: () => {
          clearTimeout(timeout);
          finish();
        },
      });
    } catch {
      clearTimeout(timeout);
      finish();
    }
  });
}

function getDocumentBody() {
  if (document.body) return Promise.resolve(document.body);

  return new Promise(resolve => {
    document.addEventListener('DOMContentLoaded', () => resolve(document.body), { once: true });
  });
}
